import type { BoardSample, ChapterBoard } from "../../board";
import { boardViewportXDelta } from "../../board/BoardSample/viewportDelta";
import { activeBeat, type BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import {
  MARKER_DIAMETER,
  markerDiscAxisOffsetY,
  timelineRailAxisY,
} from "../MarkerMetrics/metrics";
import { markerRailPose } from "../../rail/RailPose/pose";
import { computeSceneMotion } from "@/lib/timeline/scene/SceneMotion/motion";

/** Negative: the badge sits on the top of its own disc, where it cannot cover anyone's name. */
export const POV_INDICATOR_GAP_PX = -42;

export interface PovCharacterAnchor {
  anchorX: number;
  topY: number;
}

export function povCharacterIdAtPlayhead(
  layout: BookTimelineLayout,
  playheadMs: number,
): string | undefined {
  return activeBeat(layout, playheadMs)?.povCharacterId;
}

/** Stage position for the POV badge on the POV character's disc. */
export function povCharacterAnchor(
  layout: BookTimelineLayout,
  playheadMs: number,
  viewportWidth: number,
  board?: ChapterBoard,
  sample?: BoardSample,
): PovCharacterAnchor | null {
  const povId = povCharacterIdAtPlayhead(layout, playheadMs);
  if (povId === undefined) {
    return null;
  }
  if (sample !== undefined && board !== undefined) {
    return boardPovCharacterAnchor(board, sample, viewportWidth, povId);
  }
  return layoutPovCharacterAnchor(layout, playheadMs, viewportWidth, povId);
}

function layoutPovCharacterAnchor(
  layout: BookTimelineLayout,
  playheadMs: number,
  viewportWidth: number,
  povId: string,
): PovCharacterAnchor | null {
  const pose = markerRailPose(povId, playheadMs, layout.rail);
  if (pose === null) {
    return null;
  }
  const motion = computeSceneMotion(
    playheadMs,
    viewportWidth,
    layout.totalDurationMs,
  );
  const axisY = timelineRailAxisY();
  const discRadius = MARKER_DIAMETER / 2;
  const axisOffset = markerDiscAxisOffsetY();
  const centerX = motion.groupCenterX + pose.dx;
  const centerY = axisY + pose.railDy + pose.dy;
  const discTop = centerY - discRadius - axisOffset;
  return {
    anchorX: centerX,
    topY: discTop - POV_INDICATOR_GAP_PX,
  };
}

function boardPovCharacterAnchor(
  board: ChapterBoard,
  sample: BoardSample,
  viewportWidth: number,
  povId: string,
): PovCharacterAnchor | null {
  const entry = sample.characters.find((item) => item.characterId === povId);
  if (entry === undefined || !entry.pose.visible) {
    return null;
  }
  const deltaX = boardViewportXDelta(
    viewportWidth,
    board.referenceViewportWidth,
  );
  return {
    anchorX: entry.pose.x + deltaX,
    topY: entry.pose.y - POV_INDICATOR_GAP_PX,
  };
}